"use client";

import { useState } from "react";
import { Mail, ArrowLeft } from "lucide-react";
import { useUser } from "@/lib/context/user-context";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ForgotPasswordDialog = ({ open, onOpenChange }: ForgotPasswordDialogProps) => {
  const { t, lang } = useI18n() as any;
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { resetPassword } = useUser();

  const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setEmailError("");
    
    if (!validateEmail(email)) {
      setEmailError(t('auth.errors.invalidEmail'));
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(email);
      setIsEmailSent(true);
    } catch (error) {
      console.error('Error resetting password:', error);
      // Error is handled in resetPassword method with toast
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setEmailError("");
    setIsEmailSent(false);
    setIsLoading(false);
    onOpenChange(false);
  };

  const handleBackToLogin = () => {
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            {isEmailSent ? t('auth.reset.sentTitle') : t('auth.reset.title')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {isEmailSent ? t('auth.reset.sentDesc') : t('auth.reset.enterDesc')}
          </DialogDescription>
        </DialogHeader>

        {isEmailSent ? (
          <div className="space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
            </div>
            
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">{t('auth.reset.sentTo')}</p>
              <p className="font-medium text-foreground">{email}</p>
            </div>

            <div className="text-center text-xs text-muted-foreground">
              <p>{t('auth.reset.notReceived')}</p>
            </div>

            <button
              onClick={handleBackToLogin}
              className="w-full py-3 px-4 bg-muted hover:bg-muted/80 text-foreground rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('auth.reset.backToSignIn')}</span>
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="reset-email" className="block text-sm font-medium text-foreground">{t('auth.form.email')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                </div>
                <input
                  type="email"
                  id="reset-email"
                  placeholder={t('auth.form.emailPlaceholder')}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-background ${
                    emailError 
                      ? "border-destructive focus:ring-destructive" 
                      : "border-input hover:border-primary/50"
                  }`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!emailError}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              {emailError && (
                <p className="text-destructive text-sm flex items-center gap-1">
                  <span className="w-1 h-1 bg-destructive rounded-full"></span>
                  {emailError}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                  isLoading 
                    ? "bg-muted cursor-not-allowed text-muted-foreground" 
                    : "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin"></div>
                    {t('auth.reset.sending')}
                  </div>
                ) : (
                  t('auth.reset.sendLink')
                )}
              </button>

              <button
                type="button"
                onClick={handleClose}
                className="w-full py-3 px-4 bg-muted hover:bg-muted/80 text-foreground rounded-lg font-medium transition-colors"
                disabled={isLoading}
              >
                {t('auth.reset.cancel')}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
